import { useState, useEffect, useRef, useMemo } from 'react';
import type { AgentList, AgentFilters } from 'utils/agent-endpoint';
import InputWithCountrySelect from './input-with-country-select/InputWithCountrySelect';
import InputWithSearchIcon from './input-with-search-icon/InputWithSearchIcon';
import PaginationNav from 'components/control/pagination-nav';
import * as AgentActions from 'utils/agent-endpoint';
import { CountryCodeList } from 'types/countries';
import { PAGE_SIZE, AgentType } from 'utils/agent-endpoint';
import AgentCard from './agent-card/AgentCard';
import { useRouter } from 'next/router';
import NextImage from 'next/image';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_VISIT_AGENT_SEARCH_PAGE } from 'constants/events';
import { agentSearchIds } from 'constants/test-constants';
import { isClientSide } from 'utils/host-config';
import SkeletonAgentCard from './agent-card/SkeletonAgentCard';
import SkeletonResultCount from './skeleton/SkeletonResultCount';
import { generateAgentStyles } from 'utils/agent-name-obfuscator';
import AgentFilterDropdown from './agent-filter-dropdown/AgentFilterDropdown';
import { useRecaptcha } from 'hooks/use-recaptcha';
import styles from './style.module.scss';
import { HeadDataType } from 'components/head-data';
import { useIsMobile } from 'hooks/use-size-class';
import { getTenantAgentSearchConfig } from '@tenants/utils/configs';
import { getTenantById, isTenantExpInService, type TenantId } from 'tenants';
import { useTranslation } from 'next-i18next';
import { AGENT_SEARCH_NAMESPACE, LOCALE_AGENT_SEARCH_PATHS } from 'types/locales';
import { AgentObservabilityService } from 'observability';

/**
 * Agent search page props
 * @param {AgentList[]} featureAgents - List of featured agents initially displayed on the page
 * @param {CountryCodeList} initialCountry - If URL query contained country code or we get it from site location
 * @param {string} sessionId - sessionId used for agent pagination performance
 * @param {HeadDataType} headerData - Header data for the page
 * @param {AgentType} agentType - Type of search page (Agent or Commercial)
 * @param {TenantId} tenantId - Current tenant id
 */
export interface AgentSearchProps {
  initialCountry: CountryCodeList;
  sessionId: string;
  headerData: HeadDataType;
  agentType: AgentType;
  tenantId: TenantId;
}

interface AgentQueryParams {
  [key: string]: string | undefined;
  page: string;
  country: string;
  location?: string;
  name?: string;
  m?: string; // 'm' for military filter, 't' for true
}

// Add session storage key for filter preference
const FILTER_CHANGED_KEY = 'agentFilterChanged';

const AgentSearch = ({ initialCountry, sessionId, agentType = AgentType.RESIDENTIAL, tenantId }: AgentSearchProps) => {
  const router = useRouter();
  const tenant = getTenantById(tenantId);
  const agentSearchConfig = getTenantAgentSearchConfig(tenant);
  const { scriptLoaded, RecaptchaScript } = useRecaptcha();
  const { t } = useTranslation(AGENT_SEARCH_NAMESPACE);

  const shouldFetchFeatured = useRef(true);
  const shouldFetchAgents = useRef(true);
  const isCommercial = agentType === AgentType.COMMERCIAL;
  const country = router.query.country as CountryCodeList || initialCountry;
  const selectedLocation = router.query.location ? decodeURIComponent(router.query.location as string) : '';

  const [featuredAgents, setFeaturedAgents] = useState<AgentList[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(() => parseInt(router.query.page as string, 10) || 1);
  const [locationQuery, setLocationQuery] = useState(selectedLocation);
  const [agents, setAgents] = useState<AgentList[]>([]);
  const [selectedName, setSelectedName] = useState(() =>
    router.query.name ? decodeURIComponent(router.query.name as string) : '');
  const [nameQuery, setNameQuery] = useState(selectedName);
  const [isLoading, setIsLoading] = useState(false);
  const isFeatured = !selectedLocation && !selectedName && !isCommercial;
  const [agentStyles, setAgentStyles] = useState<string>('');
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<AgentFilters>(() => {
    // First check if we have a URL parameter
    if (router.query.m !== undefined) {
      return {
        isMilnet: router.query.m === 't',
      };
    }

    // Check if user has changed filter before (stored in session storage)
    if (isClientSide() && sessionStorage.getItem(FILTER_CHANGED_KEY) === 'true') {
      // Get the last known preference from session storage
      const lastPreference = sessionStorage.getItem('agentMilnetPreference');
      if (lastPreference !== null) {
        return {
          isMilnet: lastPreference === 'true',
        };
      }
    }

    // If no URL parameter and no saved preference, use tenant-based default only for InService
    return {
      isMilnet: isTenantExpInService(tenantId),
    };
  });

  const countryName = country === CountryCodeList.CANADA ? t(LOCALE_AGENT_SEARCH_PATHS.COUNTRIES_CANADA, { defaultValue: 'Canada' }) : t(LOCALE_AGENT_SEARCH_PATHS.COUNTRIES_UNITED_STATES, { defaultValue: 'United States' });
  const defaultAgentName = 'eXp Agent';

  // Do no show country select for the InService and Commercial tenants
  const showCountrySelect = agentSearchConfig?.showCountrySelect ?? true;

  // Use customized header title for InService tenant or fall back to config
  const headerTitle = (() => {
    if (isTenantExpInService(tenantId) && filters.isMilnet) {
      return t(LOCALE_AGENT_SEARCH_PATHS.PAGE_TITLE_MILITARY_EXPERTS, { defaultValue: 'Military Real Estate Experts' });
    }
    
    // If tenant has a custom pageTitle, use it as-is (for backwards compatibility)
    if (agentSearchConfig?.pageTitle) {
      return agentSearchConfig.pageTitle;
    }
    
    // Use translated title
    const agentTypeName = agentSearchConfig?.agentTypeName ?? defaultAgentName;
    if (isCommercial) {
      return t(LOCALE_AGENT_SEARCH_PATHS.PAGE_TITLE_SEARCH_ALL_COMMERCIAL, { agentType: agentTypeName, defaultValue: `Search All ${agentTypeName}` });
    }
    return t(LOCALE_AGENT_SEARCH_PATHS.PAGE_TITLE_SEARCH_ALL, { country: countryName, agentType: agentTypeName, defaultValue: `Search All ${agentTypeName} in ${countryName}` });
  })();

  const updatePathQuery = (state: Partial<AgentQueryParams> | null = null) => {
    if (!state) return;
    let params: AgentQueryParams = {
      page: page.toString(),
      country,
      m: filters.isMilnet ? 't' : 'f',
    };

    if (locationQuery !== '') params.location = locationQuery;
    if (nameQuery !== '') params.name = nameQuery;

    if (state) params = { ...params, ...state };

    const currentQuery = router.query;
    const queryChanged =
      currentQuery.country !== params.country ||
      currentQuery.page !== params.page ||
      currentQuery.m !== params.m ||
      currentQuery.location !== params.location ||
      currentQuery.name !== params.name;

    if (queryChanged) {
      if (params.location === '') delete params.location;
      if (params.name === '') delete params.name;
      if (!params.m) delete params.m;

      router.push(
        {
          pathname: router.pathname,
          query: params,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const updateCountry = (country: CountryCodeList) => {
    setLocationQuery('');
    setSelectedName('');
    setNameQuery('');
    setPage(1);
    shouldFetchAgents.current = true;
    updatePathQuery({ country, location: '', name: '', page: '1' });
  };

  const handleSelectLocation = (location: string) => {
    setLocationQuery(location);
    setPage(1);
    shouldFetchAgents.current = true;
    updatePathQuery({ location, page: '1' });
  };

  const handleSelectName = (name: string) => {
    setSelectedName(name);
    setNameQuery(name);
    setPage(1);
    shouldFetchAgents.current = true;
    updatePathQuery({ name, page: '1' });
  };

  const handleFilterChange = (newFilters: AgentFilters) => {
    setFilters(newFilters);
    setPage(1);
    shouldFetchAgents.current = true;

    // Save to session storage that user has changed the filter
    if (isClientSide()) {
      sessionStorage.setItem(FILTER_CHANGED_KEY, 'true');
      sessionStorage.setItem('agentMilnetPreference', String(newFilters.isMilnet));
    }

    updatePathQuery({ m: newFilters.isMilnet ? 't' : 'f', page: '1' });
  };

  const AgentHeader = useMemo(() => {
    return (
      <div className={`${styles['agent-header']}`}>
        <div className={`${styles['exp-header-banner']}`}>
          <NextImage
            key="exp-banner-white-no-text-1"
            fill
            src={isMobile ? agentSearchConfig?.headerImage.mobileSrc ?? '/next/assets/images/agent-search/exp-banner-white-no-text.png' : agentSearchConfig?.headerImage.src ?? '/next/assets/images/agent-search/exp-banner-white-no-text.png'}
            alt={agentSearchConfig?.headerImage.alt ?? 'eXp Realty Logo'}
            priority={true}
          />
        </div>
        <h1 className={styles['agent-header-title']} data-testid={agentSearchIds.agentSearchHeader}>
          {headerTitle}
        </h1>

        <div className={styles.inputs}>
          <InputWithCountrySelect
            onCountryChange={updateCountry}
            country={country}
            onLocationSelect={handleSelectLocation}
            locationQuery={locationQuery}
            updateLocationQuery={(query: string) => setLocationQuery(query)}
            allowCountryChange={showCountrySelect}
          />

          <InputWithSearchIcon
            onSelectName={handleSelectName}
            country={country}
            nameQuery={nameQuery}
            isCommercial={isCommercial}
            filters={filters}
          />

          {/* Since this still is test feature, we need to hide it for all tenants except InService */}
          {isTenantExpInService(tenantId) &&
          <AgentFilterDropdown
            selectedFilters={filters}
            onFilterChange={handleFilterChange}
            isCommercial={isCommercial}
          />}
        </div>
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMobile,
    agentSearchConfig,
    headerTitle,
    country,
    locationQuery,
    nameQuery,
    isCommercial,
    filters,
  ]);

  useEffect(() => {
    const fetchFeaturedAgents = async () => {
      if (isCommercial || isLoading) return;
      setIsLoading(true);
      shouldFetchFeatured.current = false;
      const response = await AgentActions.featuredAgents(country, undefined, filters);
      if (!(response instanceof Error)) {
        setFeaturedAgents(response.agents);
      }
      setIsLoading(false);
    };

    if (!selectedLocation && !selectedName && !isCommercial && scriptLoaded && isFeatured && shouldFetchFeatured.current) {
      fetchFeaturedAgents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, scriptLoaded, filters, selectedName, selectedLocation]);

  // Sync selectedName with router query changes (for navigation back from agent profile)
  useEffect(() => {
    if (!router.isReady) return;
    const name = router.query.name ? decodeURIComponent(router.query.name as string) : '';
    setSelectedName(name);
  }, [router.query.name, router.isReady]);

  // Sync nameQuery with selectedName changes
  useEffect(() => {
    setNameQuery(selectedName);
  }, [selectedName]);

  // Reset shouldFetchAgents when router query changes (for navigation back from agent profile)
  useEffect(() => {
    if (router.isReady) {
      shouldFetchAgents.current = true;
    }
  }, [router.query.name, router.query.location, router.isReady]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (isLoading) return;
      shouldFetchAgents.current = false;
      setIsLoading(true);

      const params = {
        name: selectedName,
        location: selectedLocation,
        country,
        page,
        session_id: sessionId,
        agentType,
        filters,
      };
      const data = await AgentActions.searchAgents(params);

      if (!(data instanceof Error)) {
        setAgents(data.agents);
        setCount(data.count);
      } else {
        AgentObservabilityService.trackSearchFailure({
          error: data,
          searchCriteria: {
            name: selectedName || undefined,
            location: selectedLocation || undefined,
            isCommercial,
          },
        });
        setAgents([]);
        setCount(0);
      }
      setIsLoading(false);
    };

    if (scriptLoaded && shouldFetchAgents.current && router.isReady
      && (selectedName.length > 0 || selectedLocation.length > 0 || isCommercial)) {
      updatePathQuery();
      fetchAgents();
    } else if (scriptLoaded && router.isReady && selectedName.length === 0 && selectedLocation.length === 0 && !isCommercial) {
      // Clear agents to show featured agents
      setAgents([]);
      setCount(0);
      // Trigger featured agents fetch
      shouldFetchFeatured.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName, selectedLocation, page, scriptLoaded, filters]);

  useEffect(() => {
    if (isFeatured && !isCommercial) trackEvent(GTM_VISIT_AGENT_SEARCH_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const combined = [...featuredAgents, ...agents];
    if (combined.length) {
      setAgentStyles(generateAgentStyles(combined));
    }
  }, [featuredAgents, agents]);

  const handleUpdatePageNumber = (pageNumber: number) => {
    shouldFetchAgents.current = true;
    setPage(pageNumber);
    updatePathQuery({ page: pageNumber.toString() });
  };

  const renderCount = (): string => {
    if (isFeatured) {
      const descriptor = filters.isMilnet ? agentSearchConfig?.featuredAgentDescriptor : 'eXp';
      return t(LOCALE_AGENT_SEARCH_PATHS.RESULTS_FEATURED_AGENTS, { count: 16, country: countryName, descriptor, defaultValue: `16 Featured ${descriptor} Agents in ${countryName}` });
    }
    
    const agentTypeName = agentSearchConfig?.agentTypeName ?? (isCommercial ? 'Commercial Advisor' : defaultAgentName);
    
    if (isCommercial) {
      return t(LOCALE_AGENT_SEARCH_PATHS.RESULTS_AGENT_TYPE_ONLY, { count, agentType: agentTypeName, defaultValue: `${count} ${agentTypeName}` });
    }
    
    if (selectedLocation.length > 0) {
      const location = selectedLocation.toLowerCase();
      if (location === 'washington, dc') {
        return t(LOCALE_AGENT_SEARCH_PATHS.RESULTS_AGENT_COUNT_DC, { count, agentType: agentTypeName, defaultValue: `${count} ${agentTypeName} in Washington, DC` });
      }
      const locationName = selectedLocation.split(',')[0];
      return t(LOCALE_AGENT_SEARCH_PATHS.RESULTS_AGENT_COUNT, { count, location: locationName, agentType: agentTypeName, defaultValue: `${count} ${agentTypeName} in ${locationName}` });
    }
    
    return t(LOCALE_AGENT_SEARCH_PATHS.RESULTS_AGENT_TYPE_ONLY, { count, agentType: agentTypeName, defaultValue: `${count} ${agentTypeName}` });
  };

  return (
    <div className={styles.component}>
      {agentStyles && <style dangerouslySetInnerHTML={{ __html: agentStyles }} />}
      {AgentHeader}

      {(!scriptLoaded || isLoading) && (
        <>
          <SkeletonResultCount />
          <div className={styles['search-results-container']}>
            {[...Array(4)].map((_, index) => (
              <SkeletonAgentCard key={index} />
            ))}
          </div>
        </>
      )}

      {(scriptLoaded && !isLoading) && (
        <>
          <p className={styles['result-count']} data-testid={agentSearchIds.agentsResultCount}>{renderCount()}</p>
          <div className={styles['search-results-container']}>
            {featuredAgents && featuredAgents.length > 0 && isFeatured && (
              <>
                {featuredAgents.map((agent, index) => (
                  <AgentCard key={agent.id || index} agent={agent} isCommercial={isCommercial} />
                ))}
              </>
            )}

            {(agents && Array.isArray(agents) && agents.length > 0) &&
              agents.map((agent, index) => (
                <AgentCard key={agent.id || index} agent={agent} isCommercial={isCommercial} />
              ))}
          </div>
          {(!agents || agents.length === 0) && !isFeatured && (
            <div className={styles['no-results']} data-testid={agentSearchIds.agentNoResultsMessage}>
              <NextImage
                src={`/next/assets/images/agent-search/no-agent-results${isCommercial ? '-commercial' : ''}.png`}
                alt="No results found"
                width={237}
                height={176}
              />
              <h1>{t(LOCALE_AGENT_SEARCH_PATHS.NO_RESULTS_TITLE, { defaultValue: 'No agents found' })}</h1>
              <p>{t(LOCALE_AGENT_SEARCH_PATHS.NO_RESULTS_SUBTITLE, { defaultValue: 'Try adjusting your search criteria' })}</p>
            </div>
          )}
        </>
      )}
      <PaginationNav
        pageNumber={page}
        totalPages={Math.ceil(count / PAGE_SIZE) || 1}
        setPageNumber={handleUpdatePageNumber}
      />
      {RecaptchaScript}
    </div>
  );
};

export default AgentSearch;
